// Centralized date/time formatting for admin surfaces.
// All dates are displayed in America/Mexico_City timezone because that is
// where the admin operates.
//
// Implementation uses native Intl.DateTimeFormat for zero-dependency TZ support.
// Previous dependency consideration: date-fns@^4.1.0 is in package.json but
// date-fns-tz v3 is only compatible with date-fns v3, so native Intl is the
// clean path.

const ADMIN_TIMEZONE = 'America/Mexico_City';

// Memoize formatters — constructing Intl.DateTimeFormat is comparatively expensive.
const _dateFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: ADMIN_TIMEZONE,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const _timeFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: ADMIN_TIMEZONE,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const _dateTimeFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: ADMIN_TIMEZONE,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/**
 * Parse any date-ish input into a Date, or null.
 * Critical fix: if an ISO string lacks a timezone marker we append 'Z' and
 * treat as UTC (legacy backend analytics_events strings were naive UTC).
 *
 * PILOTO-1B (firma #80) — EXPORTADA, y por una razón concreta. `lib/groups.js`
 * necesita comparar DÍAS DE CALENDARIO («vence hoy» vs «vence mañana») sobre la
 * misma marca que estos formateadores luego pintan. Si groups.js hiciera su
 * propio `new Date(iso)`, una marca ISO SIN zona la leería como hora LOCAL
 * mientras esto la lee como UTC — y el badge diría «mañana» junto a una hora de
 * hoy. Una sola regla de parseo, un solo sitio.
 */
export function parseDateish(input) {
  return parseDateInput(input);
}

function parseDateInput(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === 'number') {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input !== 'string') return null;

  // Detect trailing timezone marker: 'Z', 'z', '+HH', '+HHMM', '+HH:MM', '-HH…'.
  // We only check the last 6 chars to avoid matching the '-' in the date portion.
  const tail = input.slice(-6);
  const hasTzMarker = /[Zz]$/.test(input) || /[+\-]\d{2}:?\d{2}$/.test(tail);
  const normalized = hasTzMarker ? input : `${input}Z`;

  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

/** "Apr 20, 2026" in America/Mexico_City. */
export function formatDate(input) {
  const d = parseDateInput(input);
  return d ? _dateFmt.format(d) : '';
}

/** "10:54 AM" in America/Mexico_City. */
export function formatTime(input) {
  const d = parseDateInput(input);
  return d ? _timeFmt.format(d) : '';
}

/** "Apr 20, 2026, 10:54 AM" in America/Mexico_City. */
export function formatDateTime(input) {
  const d = parseDateInput(input);
  return d ? _dateTimeFmt.format(d) : '';
}

/**
 * Relative time, clamped so a future/very-recent timestamp renders as
 * "just now" (never "-20433s ago").
 */
export function formatRelative(input) {
  const d = parseDateInput(input);
  if (!d) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 5000) return 'just now';
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const day = Math.round(h / 24);
  return `${day}d ago`;
}

/** Full ISO string for use in a title/tooltip attribute. */
export function formatIsoTooltip(input) {
  const d = parseDateInput(input);
  return d ? d.toISOString() : '';
}

/**
 * USER-facing long date localized to the ACTIVE i18n language, not the
 * browser locale: "August 7, 2026" (en) / "7 de agosto de 2026" (es).
 * Unlike the admin formatters above it has no pinned timezone (viewer's own).
 * Pass `language` explicitly or let it read the i18n singleton.
 */
export function formatDateLong(input, language) {
  const d = parseDateInput(input);
  if (!d) return '';
  // Lazy require so the admin-only consumers of this module don't need i18n
  // initialized in tests.
  const lang = language || require('../i18n').default?.language || 'en';
  return d.toLocaleDateString(String(lang).startsWith('es') ? 'es-MX' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * PILOTO-1B (firma #80) — LA PAREJA CORTA, USER-FACING.
 *
 * Misma clase que `formatDateLong` y NO la de los formateadores de arriba: sin
 * zona horaria fijada (la del navegador — un profesor en Tijuana lee su hora, no
 * la del admin en CDMX) y localizada al IDIOMA ACTIVO de i18n, no al del
 * sistema. Los de arriba están clavados a America/Mexico_City a propósito porque
 * sirven al panel de admin, donde el sujeto es siempre Carlos.
 *
 * Dónde se usan: el vencimiento del código de grupo y la fecha de unión de un
 * alumno (lib/groups.js).
 */
function userLocale(language) {
  // Lazy require, igual que formatDateLong: los consumidores de admin de este
  // módulo no tienen i18n inicializado en sus tests.
  //
  // Y CON RESPALDO, que es lo único que esto añade sobre su hermana: estas dos
  // funciones las llama `lib/groups.js` desde pantallas y desde un modal del
  // chasis, y el `language` puede llegar `undefined` si alguien monta una de
  // ellas con un doble de `useTranslation` a medias. Sin el respaldo, ese caso
  // arrastraría la inicialización REAL de i18next (con su backend HTTP) dentro
  // de un test que no la pidió. `formatDateLong` NO se toca: su camino queda
  // byte-idéntico.
  let lang = language;
  if (!lang) {
    try { lang = require('../i18n').default?.language; } catch (e) { /* sin i18n: inglés */ }
  }
  return String(lang || 'en').startsWith('es') ? 'es-MX' : 'en-US';
}

/** "Aug 18, 2026" (en) / "18 ago 2026" (es), en la zona del navegador. */
export function formatDateShort(input, language) {
  const d = parseDateInput(input);
  if (!d) return '';
  return d.toLocaleDateString(userLocale(language),
    { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "7:40 PM" (en) / "7:40 p.m." (es), en la zona del navegador. */
export function formatTimeShort(input, language) {
  const d = parseDateInput(input);
  if (!d) return '';
  return d.toLocaleTimeString(userLocale(language), { hour: 'numeric', minute: '2-digit' });
}
